import { useState, useEffect } from "react";
import type { CurrentMfe } from "./components/Router/state";
import type * as featureList from "./portals/messagemedia/featureList";
import { MaxBreakPoints } from "./style/constants";
import type {
  NavigatorWithNetworkInfo,
  NetworkInformation,
} from "./types/navigator";

export type Features = (typeof featureList)[keyof typeof featureList];

export const userPermissionHasAccess = (
  routePermissions: readonly string[] | undefined,
  userPermissions: string[] = []
) =>
  routePermissions
    ? routePermissions.some((permission) =>
        permission.endsWith("*")
          ? userPermissions.some((userPermission) =>
              userPermission.startsWith(permission.replace("*", ""))
            )
          : userPermissions.includes(permission)
      )
    : true;

const getParsedValidatedValue = <Data>(
  key: string,
  value: string | null,
  validator: (x: unknown) => x is Data
): Data | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsedValue = JSON.parse(value);
    if (validator(parsedValue)) {
      return parsedValue;
    } else {
      console.warn("Failed to validate localStorage value for key", key);
      return undefined;
    }
  } catch {
    console.warn(`Failed to parse localStorage value for key ${key}`);
    return undefined;
  }
};

/**
 * Returns tuple with value inside storage or default and a function to update the value in the storage
 * The hook listens for storage events to know if the value should be updated
 */
export function useLocalStorage<Data>(
  key: string,
  validator: (x: unknown) => x is Data,
  defaultValue: Data
) {
  const [value, setValue] = useState<Data>(() => {
    const value = window.localStorage.getItem(key);
    return getParsedValidatedValue(key, value, validator) ?? defaultValue;
  });

  useEffect(() => {
    const storageEventListener = (event: StorageEvent) => {
      if (event.key === key) {
        const parsedValidValue = getParsedValidatedValue(
          key,
          event.newValue,
          validator
        );

        if (parsedValidValue) {
          setValue(parsedValidValue);
        }
      }
    };

    window.addEventListener("storage", storageEventListener);
    return () => window.removeEventListener("storage", storageEventListener);
    // Should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStorageValue = (newValue: Data) => {
    try {
      localStorage.setItem(key, JSON.stringify(newValue));

      // Needs to dispatch an event for the change to be propagated on the same tab.
      // See: https://stackoverflow.com/a/65348883
      window.dispatchEvent(
        new StorageEvent("storage", { key, newValue: JSON.stringify(newValue) })
      );
    } catch (error) {
      console.error(`Failed to set localStorage value for key ${key}`, error);
    }
  };

  return [value, setStorageValue] as const;
}

const compareSemver = (v1: string, v2: string) =>
  v1.localeCompare(v2, undefined, { numeric: true });

export const semverIsLowerThan = (v1: string, v2: string) =>
  compareSemver(v1, v2) < 0;

export const getMountId = (currentMfe: CurrentMfe) =>
  `${currentMfe.name}-${currentMfe.entry}-${currentMfe.path}`;

export const getNetworkConnection = (): NetworkInformation | null => {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as NavigatorWithNetworkInfo;
  return nav?.connection || nav?.mozConnection || nav?.webkitConnection || null;
};

type DeviceType = "mobile" | "tablet" | "desktop";

export const getDeviceType = (): DeviceType => {
  try {
    const ua = navigator.userAgent;
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const width = window.innerWidth;

    // Check for tablet in user agent
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "tablet";
    }

    // Check for mobile in user agent
    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    ) {
      return "mobile";
    }

    // Fallback to screen size if touch device
    if (isTouch && width <= MaxBreakPoints.MEDIUM) {
      return "mobile";
    }
    if (
      isTouch &&
      width > MaxBreakPoints.MEDIUM &&
      width <= MaxBreakPoints.LARGE
    ) {
      return "tablet";
    }

    return "desktop";
  } catch (_) {
    //
  }
  return "desktop";
};
